import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Heading,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {getPDPath} from '../../../utils/custom-utils'

export const CountDownTimer = ({
    backgroundColor = 'transparent',
    headingText,
    headingfontStyle,
    secondButtonfontStyle,
    subHeadingText,
    headingTextColor,
    subHeadingfontStyle,
    headingfontWeight,
    firstButtonfontStyle,
    firstButtonfontWeight,
    firstButtonText,
    subHeadingfontWeight,
    countDown,
    secondButtonText,
    secondButtonfontWeight,
    firstButtonLink,
    secondButtonLink,
    subHeadingTextColor,
    firstButtonTextColor,
    firstButtonBackgroundColor,
    secondButtonTextColor,
    secondButtonBackgroundColor,
    custom
}) => {
    const [timeLeft, setTimeLeft] = useState({hours: '00', minutes: '00', seconds: '00'})
    const [isExpired, setIsExpired] = useState(false)

    const styles = useMultiStyleConfig('countDownTimer')
    const layoutStyles = useMultiStyleConfig('containerStyles')
    const {site, locale} = useMultiSite()
    const himeImageUrl = custom?.himeImage
    const firstButtonCategoryUrl = getPDPath(firstButtonLink, site, locale)
    const secondButtonCategoryUrl = getPDPath(secondButtonLink, site, locale)
    const segment = useSegment()

    useEffect(() => {
        const updateCountdown = () => {
            const deadline = new Date(countDown).getTime()
            const now = new Date().getTime()
            const differenceTime = deadline - now

            if (differenceTime <= 0) {
                setIsExpired(true)
                setTimeLeft({hours: '00', minutes: '00', seconds: '00'})
                return
            }

            setIsExpired(false)

            const hours = String(
                Math.floor((differenceTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
            ).padStart(2, '0')
            const minutes = String(
                Math.floor((differenceTime % (1000 * 60 * 60)) / (1000 * 60))
            ).padStart(2, '0')
            const seconds = String(Math.floor((differenceTime % (1000 * 60)) / 1000)).padStart(
                2,
                '0'
            )

            setTimeLeft({hours, minutes, seconds})
        }

        const interval = setInterval(updateCountdown, 1000)
        updateCountdown() // Initialize immediately

        return () => clearInterval(interval)
    }, [countDown])

    return (
        <Box
            data-test-id="countdowntimer-block"
            style={{background: backgroundColor}}
            {...styles.countdowntimerBlock}
            onClick={(event) => {
                // segment
                segment.sendPanelSelectedEvent(event, {})
            }}
        >
            <Link
                className={`${secondButtonLink ? '' : 'no-event-link'}`}
                href={secondButtonCategoryUrl}
                aria-label="Go to Link"
                {...styles.mobileCountdownLink}
            />
            <Box
                className="countdowntimer-inner-block"
                {...styles.countdowntimerInnerBlock}
                py={{base: '0px', sm: '11px', md: '12px'}}
            >
                {himeImageUrl && (
                    <Box
                        className="image-block"
                        style={{
                            backgroundImage: `url(${himeImageUrl?.disBaseLink})`
                        }}
                        {...styles.imageBlock}
                    />
                )}
                <Box
                    className="countdowntimer-text-timer-block"
                    sx={{
                        ...styles.countdowntimerTextTimerBlock,
                        ...layoutStyles.containerStyles
                    }}
                >
                    <Box {...styles.countdowntimerTextTimerLeftBlock}>
                        {headingText && (
                            <Heading
                                style={{
                                    color: headingTextColor?.value,
                                    fontStyle: headingfontStyle,
                                    fontWeight:
                                        headingfontWeight === 'bold' ? 800 : headingfontWeight
                                }}
                                {...styles.mainHeading}
                            >
                                {headingText}
                            </Heading>
                        )}
                        {subHeadingText && (
                            <Text
                                style={{
                                    color: subHeadingTextColor?.value,
                                    fontStyle: subHeadingfontStyle,
                                    fontWeight:
                                        subHeadingfontWeight === 'bold' ? 800 : subHeadingfontWeight
                                }}
                                {...styles.subHeading}
                            >
                                {subHeadingText}
                            </Text>
                        )}
                    </Box>
                    <Box {...styles.countdowntimerTextTimerRightBlock}>
                        {/* Show timer only when not expired */}
                        {!isExpired && countDown && (
                            <Box {...styles.clockBlock}>
                                <Box {...styles.digitsBg}>{timeLeft.hours}</Box>
                                <Box {...styles.colon}>:</Box>
                                <Box {...styles.digitsBg}>{timeLeft.minutes}</Box>
                                <Box {...styles.colon}>:</Box>
                                <Box {...styles.digitsBg}>{timeLeft.seconds}</Box>
                            </Box>
                        )}
                        {firstButtonText && (
                            <Box display={['none', 'none', 'block']} {...styles.buttonBlock}>
                                <Link
                                    className={` ${firstButtonLink ? '' : 'no-event-link'}`}
                                    href={firstButtonCategoryUrl}
                                    style={{
                                        color: firstButtonTextColor?.value,
                                        backgroundColor: firstButtonBackgroundColor?.value,
                                        fontStyle: firstButtonfontStyle,
                                        fontWeight: firstButtonfontWeight
                                    }}
                                    {...styles.countdownLink}
                                >
                                    {firstButtonText}
                                </Link>
                            </Box>
                        )}
                        {secondButtonText && (
                            <Box display={['none', 'block']} {...styles.buttonBlock}>
                                <Link
                                    className={` ${secondButtonLink ? '' : 'no-event-link'}`}
                                    href={secondButtonCategoryUrl}
                                    style={{
                                        color: secondButtonTextColor?.value,
                                        backgroundColor: secondButtonBackgroundColor?.value,
                                        fontStyle: secondButtonfontStyle,
                                        fontWeight: secondButtonfontWeight
                                    }}
                                    {...styles.countdownLink}
                                >
                                    {secondButtonText}
                                </Link>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

CountDownTimer.propTypes = {
    headingText: PropTypes.string,
    subHeadingText: PropTypes.string,
    countDown: PropTypes.string,
    backgroundColor: PropTypes.string,
    firstButtonText: PropTypes.string,
    secondButtonText: PropTypes.string,
    firstButtonLink: PropTypes.string,
    secondButtonLink: PropTypes.string,
    headingfontStyle: PropTypes.string,
    headingTextColor: PropTypes.object,
    subHeadingfontStyle: PropTypes.string,
    headingfontWeight: PropTypes.string,
    subHeadingfontWeight: PropTypes.string,
    firstButtonfontStyle: PropTypes.string,
    secondButtonfontStyle: PropTypes.string,
    firstButtonfontWeight: PropTypes.string,
    secondButtonfontWeight: PropTypes.string,
    subHeadingTextColor: PropTypes.object,
    firstButtonTextColor: PropTypes.object,
    firstButtonBackgroundColor: PropTypes.object,
    secondButtonTextColor: PropTypes.object,
    secondButtonBackgroundColor: PropTypes.object,
    custom: PropTypes.object
}

export default CountDownTimer
